<script lang="ts" setup>
import { computed, ref, watch } from 'vue'
import CButton from '@lib/components/CButton/CButton.vue'
import CIcon from '@lib/components/CIcon/CIcon.vue'
import CPopover from '@lib/components/CPopover/CPopover.vue'
import CCard from '@lib/components/CCard/CCard.vue'
import CCheckbox from '@lib/components/CCheckbox/CCheckbox.vue'
import { NSlider, NInputNumber } from 'naive-ui'
import { useI18n } from 'vue-i18n'

export type CopyOption = 'all' | 'unedited' | 'range' | null

const { t } = useI18n()

const props = withDefaults(
  defineProps<{
    disabled?: boolean
    pageCount: number
    option?: CopyOption
  }>(),
  {
    option: null,
  },
)

const emit = defineEmits<{
  copy: [option: Exclude<CopyOption, null>, range?: number[]]
}>()

const popoverTriggerButtonRef = ref<typeof CButton | null>(null)
const copyOption = ref<CopyOption>(props.option)
const range = ref<number[]>([1, props.pageCount])
const isCopyDisabled = computed(() => {
  const isValidFromRange =
    !isNaN(range.value[0]) &&
    Number.isInteger(range.value[0]) &&
    range.value[0] >= 1 &&
    range.value[0] <= range.value[1]

  const isValidToRange =
    !isNaN(range.value[1]) &&
    Number.isInteger(range.value[1]) &&
    range.value[1] >= range.value[0] &&
    range.value[1] <= props.pageCount

  return (copyOption.value === 'range' && (!isValidFromRange || !isValidToRange)) || copyOption.value === null
})

function closePopover() {
  popoverTriggerButtonRef.value?.$el.click()
}
function handleCopyOptionUpdate(option: CopyOption) {
  copyOption.value = copyOption.value === option ? null : option
}
function handleCopy() {
  emit(
    'copy',
    copyOption.value as Exclude<CopyOption, null>,
    copyOption.value === 'range' ? range.value.map((page) => page - 1) : undefined,
  )
  copyOption.value = null
  closePopover()
}

watch(
  () => props.option,
  (newVal) => {
    copyOption.value = newVal
  },
)
</script>

<template>
  <div class="c-file-viewer-copy-pages-metadata w-full" @keydown.esc="closePopover">
    <CPopover class="min-w[400px]" placement="bottom-end" scrollable :showArrow="false" trigger="click">
      <template #trigger>
        <CButton ref="popoverTriggerButtonRef" class="w-full" :disabled="disabled" type="primary">
          {{ t('canvasUI.components.fileViewer.pageMetadata.form.copyChanges') }}
        </CButton>
      </template>
      <CCard :bordered="false" size="medium">
        <div class="flex flex-col gap-2">
          <CCheckbox
            :checked="copyOption === 'all'"
            :label="t('canvasUI.components.fileViewer.pageMetadata.form.copyOptions.allPages')"
            @update:checked="() => handleCopyOptionUpdate('all')"
          />
          <CCheckbox
            :checked="copyOption === 'unedited'"
            :label="t('canvasUI.components.fileViewer.pageMetadata.form.copyOptions.uneditedPages')"
            @update:checked="() => handleCopyOptionUpdate('unedited')"
          />
          <CCheckbox
            :checked="copyOption === 'range'"
            :label="t('canvasUI.components.fileViewer.pageMetadata.form.copyOptions.customRange')"
            @update:checked="() => handleCopyOptionUpdate('range')"
          />
          <div class="flex flex-col gap-1 mt-1">
            <NSlider
              v-model:value="range"
              :disabled="copyOption !== 'range'"
              :max="pageCount"
              :min="1"
              range
              :step="1"
            />
            <div class="flex gap-2">
              <NInputNumber
                v-model:value="range[0]"
                :disabled="copyOption !== 'range'"
                :max="range[1]"
                :min="1"
                :placeholder="''"
                :precision="0"
              >
                <template #prefix>
                  <div class="flex items-center gap-2 color-text3">
                    <CIcon icon="page" size="16px" />
                    <div>
                      {{ t('canvasUI.components.fileViewer.pageMetadata.form.copyOptions.from') }}
                    </div>
                  </div>
                </template>
              </NInputNumber>
              <NInputNumber
                v-model:value="range[1]"
                :disabled="copyOption !== 'range'"
                :max="pageCount"
                :min="range[0]"
                :placeholder="''"
                :precision="0"
              >
                <template #prefix>
                  <div class="flex items-center gap-2 color-text3">
                    <CIcon icon="page" size="16px" />
                    <div>
                      {{ t('canvasUI.components.fileViewer.pageMetadata.form.copyOptions.to') }}
                    </div>
                  </div>
                </template>
              </NInputNumber>
            </div>
          </div>
        </div>
        <template #footer>
          <CButton class="w-full" :disabled="isCopyDisabled" type="primary" @click="handleCopy">
            {{ t('canvasUI.components.fileViewer.pageMetadata.form.copyNow') }}
          </CButton>
        </template>
      </CCard>
    </CPopover>
  </div>
</template>
